import { type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Camera/OverlaySettings/OverlaySettings.less";
import { Card, Icon, IconSize, IconWeight, Popover, SettingsLayout, Switch, Tabs } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { isEqual } from "lodash";
import { produce } from "immer";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { selectOverlays, setOverlaysAction } from "Data/Redux/Slices/Settings/Camera/Overlays";
import BannerOverlaySettings from "Components/ConfigLayout/Camera/OverlaySettings/BannerOverlaySettings";
import LogoMotionOverlaySettings from "Components/ConfigLayout/Camera/OverlaySettings/LogoMotionOverlaySettings";
import LogoAlarmSettings from "Components/ConfigLayout/Camera/OverlaySettings/LogoAlarmSettings";
import BorderOverlaySettings from "Components/ConfigLayout/Camera/OverlaySettings/BorderOverlaySettings";

function OverlaySettings() {
	const dispatch = useAppDispatch();
	const overlaysSettings = useAppSelector(selectOverlays);
	const [ localOverlaysSettings, setLocalOverlaysSettings ] = useState(overlaysSettings);
	const setOverlaysSettings = () => dispatch(setOverlaysAction(localOverlaysSettings));
	const { bannerOverlay, borderOverlay, logoMotionOverlay } = localOverlaysSettings;
	const { eventTypes: bannerEventTypes } = bannerOverlay;
	const { eventTypes: borderEventTypes } = borderOverlay;
	const { eventTypes: logoMotionEventTypes } = logoMotionOverlay;
	const submitDisabled = bannerEventTypes.size === 0 || borderEventTypes.size === 0
		|| logoMotionEventTypes.size === 0;

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault();

		setOverlaysSettings();
	};

	const handleFormReset = (event: FormEvent) => {
		event.preventDefault();

		setLocalOverlaysSettings(overlaysSettings);
	};

	const handleEnabledChange = (checked: boolean) => {
		setLocalOverlaysSettings(produce(localOverlaysSettings, (draft) => {
			draft.enableOverlays = checked;
		}));
	};

	const panes = [
		{
			title: "Banner",
			content: <BannerOverlaySettings
				localOverlaySettings={ localOverlaysSettings }
				setLocalOverlaySettings={ setLocalOverlaysSettings }
			/>
		},
		{
			title: "Logo Motion",
			content: <LogoMotionOverlaySettings
				settings={ localOverlaysSettings }
				setOverlaySettings={ setLocalOverlaysSettings }
			/>
		},
		{
			title: "Logo Alarm",
			content: <LogoAlarmSettings
				localOverlaySettings={ localOverlaysSettings }
				setLocalOverlaySettings={ setLocalOverlaysSettings }
			/>
		},
		{
			title: "Border",
			content: <BorderOverlaySettings
				localOverlaySettings={ localOverlaysSettings }
				setLocalOverlaySettings={ setLocalOverlaysSettings }
			/>
		}
	];

	const titleInfo = <div className="title-info">
		Overlays are only applied to the on-screen PVM image and 4th stream.
	</div>;

	const title = (
		<div className="title">
			<span>Overlays</span>
			<Popover content={ titleInfo }>
				<Icon name="circle-info" iconWeight={ IconWeight.LIGHT } size={ IconSize.SMALLEST } />
			</Popover>
		</div>
	);

	return (
		<SettingsLayout.Form
			className="overlay-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title={ title }>
					<div className="enabled-switch">
						<Switch
							checked={ localOverlaysSettings.enableOverlays }
							onChange={ handleEnabledChange }
						/>
						<span>Enable Overlays</span>
					</div>
					<Tabs panes={ panes } />
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons
				disabled={ isEqual(localOverlaysSettings, overlaysSettings) || submitDisabled }
			/>
		</SettingsLayout.Form>
	);
}

export default memo(OverlaySettings);
